import type { LookupServiceInterface } from "@/interfaces/LookupServiceInterface";
import LookupServiceResponseTransformer from "@/transformers/LookupServiceResponseTransformer";
import type { LookupServiceResponseType, LookupServiceProviderType } from "@/types";
import { XMLParser } from "fast-xml-parser";
import { ProviderAuthenticationError } from "@/utils/errors/LookupServiceErrors";
import { handleQrzError } from "@/utils/errors/QrzErrorHandler";

const QRZ_ENDPOINT = "https://xmldata.qrz.com/xml/current";
// parseTagValue:false keeps every tag content as a string. The default `true`
// silently coerces numeric-looking text (e.g. <state>64</state> for French
// dept. 64) into a JS number, which corrupts string-typed fields like
// state/zip downstream and breaks ADIF export. The transformer coerces the
// few fields that are genuinely numeric (dxcc, lat/lon, zones).
const parser = new XMLParser({ parseTagValue: false });

class QrzService implements LookupServiceInterface {
  readonly providerName: LookupServiceProviderType = "QRZ";
  readonly requiresAuthentication: boolean = true;
  readonly requiresConnectivity: boolean = true;
  providesGridsquares: boolean = false;

  sessionKey: string;

  constructor(sessionKey: string = "") {
    this.sessionKey = sessionKey;
  }

  public async authenticate(
    username: string,
    password: string
  ): Promise<string> {
    if (!username || !password) {
      throw new ProviderAuthenticationError(this.providerName, "Missing username or password")
    }
    const response = await fetch(
      `${QRZ_ENDPOINT}?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`
    );
    const xmlData = await response.text();
    const parsedXml = this.parseXmlResponse(xmlData);

    this.handleErrors(parsedXml);

    if (this.hasSessionKey(parsedXml)) {
      this.sessionKey = this.getSessionKey(parsedXml);
      // Check if user is a subscriber
      this.providesGridsquares = parsedXml?.QRZDatabase?.Session?.SubExp !== "non-subscriber";
    }
    return this.sessionKey;
  }

  normalizeCallsignQuery(callsign: string): string {
    const [prefix, suffix] = callsign.toUpperCase().split("/");
    if (suffix && ["P", "M"].includes(suffix)) {
      return prefix
    } else {
      return encodeURIComponent(callsign);
    }
  }
  public async lookupCallsign(callsign: string): Promise<LookupServiceResponseType> {
    const normalizedCallsign = this.normalizeCallsignQuery(callsign);
    const response = await fetch(
      `${QRZ_ENDPOINT}?s=${this.sessionKey}&callsign=${normalizedCallsign}`
    );
    const xmlData = await response.text();
    const parsedXml = this.parseXmlResponse(xmlData)

    this.handleErrors(parsedXml);

    const normalizeResponse = this.normalizeResponse(parsedXml);
    return normalizeResponse;
  }
  private parseXmlResponse(xmlData: string) {
    const parsedXml = parser.parse(xmlData);
    return parsedXml;
  }

  private handleErrors(response: any): void {
    if (response?.QRZDatabase?.Session?.Error) {
      handleQrzError(this.providerName, response.QRZDatabase.Session.Error);
    }
  }

  private hasSessionKey(response: any): boolean {
    return !!this.getSessionKey(response);
  }
  private getSessionKey(response: any): string {
    return response?.QRZDatabase?.Session?.Key;
  }

  private normalizeResponse(response: any): LookupServiceResponseType {
    return LookupServiceResponseTransformer(response, this.providerName);
  }
}

export default QrzService
